import { useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  MoreVertical,
  Pencil,
  Plus,
  Trash2,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { GridLayoutCore } from "@/components/dashboard/GridLayout";
import { AddWidgetModal } from "./AddWidgetModal";
import { cn } from "@/lib/utils";
import type { DashboardSection, DashboardMode } from "@/types/dashboard";
import type { WidgetPlacement } from "@/types/widget";

export function SectionGrid({
  section,
  mode,
  siblingeSections,
  isFirst,
  isLast,
  onUpdateSection,
  onDeleteSection,
  onMoveUp,
  onMoveDown,
  onPlacementsChange,
  onAddWidget,
  onRenameWidget,
  onDuplicateWidget,
  onRemoveWidget,
  onMoveWidgetToSection,
}: {
  section: DashboardSection;
  mode: DashboardMode;
  siblingeSections: { id: string; name: string }[];
  isFirst: boolean;
  isLast: boolean;
  onUpdateSection: (patch: Partial<Pick<DashboardSection, "name" | "collapsed">>) => void;
  onDeleteSection: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onPlacementsChange: (placements: WidgetPlacement[]) => void;
  onAddWidget: (widgetId: string) => void;
  onRenameWidget: (instanceId: string, title: string) => void;
  onDuplicateWidget: (instanceId: string) => void;
  onRemoveWidget: (instanceId: string) => void;
  onMoveWidgetToSection: (instanceId: string, toSectionId: string) => void;
}) {
  const isEdit = mode === "edit";
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(section.name);
  const [addWidgetOpen, setAddWidgetOpen] = useState(false);

  function commitRename() {
    const trimmed = renameValue.trim();
    if (trimmed) onUpdateSection({ name: trimmed });
    else setRenameValue(section.name);
    setRenaming(false);
  }

  // Placements as WidgetPlacement[]
  const visiblePlacements = section.placements;

  return (
    <div
      className={cn("flex flex-col", isEdit && "rounded-lg border border-border overflow-hidden")}
    >
      {/* Section header */}
      <div className={cn("flex items-center gap-2 px-3 py-2 select-none", isEdit ? "" : "mb-2")}>
        {/* Collapse toggle */}
        <button
          onClick={() => onUpdateSection({ collapsed: !section.collapsed })}
          className="text-muted hover:text-primary transition-colors shrink-0 cursor-pointer"
          aria-label={section.collapsed ? "Expand section" : "Collapse section"}
        >
          {section.collapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </button>

        {/* Section name — or inline rename */}
        {renaming ? (
          <input
            className="flex-1 bg-transparent text-sm font-semibold text-primary outline-none border-b border-brand-cyan cursor-text select-text"
            value={renameValue}
            autoFocus
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitRename();
              if (e.key === "Escape") {
                setRenameValue(section.name);
                setRenaming(false);
              }
            }}
            onBlur={commitRename}
          />
        ) : (
          <span className="flex-1 text-sm font-semibold text-primary truncate">{section.name}</span>
        )}

        {/* Edit mode controls */}
        {isEdit && (
          <div className="flex items-center gap-1 shrink-0">
            {/* Add widget to this section */}
            <button
              onClick={() => setAddWidgetOpen(true)}
              className="flex items-center gap-1 px-2 py-1 rounded text-xs text-secondary hover:text-brand-cyan hover:bg-brand-cyan/10 transition-colors"
              title="Add widget to section"
            >
              <Plus className="h-3 w-3" />
              Add Widget
            </button>
            {/* Section 3-dot menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="p-1 rounded text-secondary hover:text-primary hover:bg-surface-elevated transition-colors cursor-pointer"
                  aria-label="Section options"
                >
                  <MoreVertical className="h-3.5 w-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-36">
                <DropdownMenuItem
                  onClick={() => {
                    setRenameValue(section.name);
                    setRenaming(true);
                  }}
                >
                  <Pencil className="h-3.5 w-3.5" />
                  Rename
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onMoveUp} disabled={isFirst}>
                  <ArrowUp className="h-3.5 w-3.5" />
                  Move up
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onMoveDown} disabled={isLast}>
                  <ArrowDown className="h-3.5 w-3.5" />
                  Move down
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={onDeleteSection}
                  className="text-rose-400 focus:text-rose-400 focus:bg-rose-500/10"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Delete section
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>

      {/* Section body */}
      {!section.collapsed && (
        <div className={cn(isEdit && "p-2")}>
          {visiblePlacements.length === 0 && isEdit ? (
            <button
              onClick={() => setAddWidgetOpen(true)}
              className="w-full py-8 rounded-lg border border-dashed border-border/50 text-sm text-muted hover:border-brand-cyan/40 hover:text-brand-cyan transition-colors"
            >
              <Plus className="h-4 w-4 mx-auto mb-1" />
              Add Widget
            </button>
          ) : (
            <GridLayoutCore
              placements={visiblePlacements}
              mode={mode}
              onLayoutChange={onPlacementsChange}
              onRenameWidget={(id, title) => onRenameWidget(id, title)}
              onDuplicateWidget={(id) => onDuplicateWidget(id)}
              onRemoveWidget={(id) => onRemoveWidget(id)}
              moveSections={siblingeSections}
              onMoveToSection={(id, sid) => onMoveWidgetToSection(id, sid)}
            />
          )}
        </div>
      )}

      {/* Add widget modal */}
      <AddWidgetModal
        open={addWidgetOpen}
        onOpenChange={setAddWidgetOpen}
        placements={visiblePlacements}
        onAdd={onAddWidget}
      />
    </div>
  );
}
